import { Component } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { of, Subject } from 'rxjs';
import { concatMap, map, takeUntil } from 'rxjs/operators';

import { UserInfo } from '../user.data';
import { UserService } from '../user.service';
import { VirtualDlgComponent } from 'app/content/virtual/dlg/virtual-dlg.component';
import { AccountService } from 'app/entry/account.service';

@Component({
    templateUrl: './user-policy-overview.component.html',
    styleUrls: ['../user.style.css'],
    standalone: false
})
export class UserPolicyOverviewComponent extends VirtualDlgComponent {
    _loading: boolean;
    _licenseUsage: number = 0;
    _availableLicenseUsage: number = 0;
    _userRoleList: { name: string, desc: string, userList?: UserInfo[] }[] = [];
    _userRoleUsageList: { roleName: string, usage: number, limit: number }[] = [];
    _activeUserRoleName: string;

    private _allUnsubscribe: Subject<void> = new Subject();

    constructor(protected accountSvc: AccountService, private route: ActivatedRoute, private location: Location, private userSvc: UserService) { 
        super(accountSvc);
    }

    ngOnInit(): void {
        super.ngOnInit();

        this._availableLicenseUsage = this.userSvc.userLicenseAmount;

        this.route.paramMap.pipe(
            takeUntil(this._allUnsubscribe)
        ).subscribe((params: ParamMap) => {
            const path: string = this.location.path();
            const lastPath: string = decodeURIComponent(path.substring(path.lastIndexOf('/') + 1));
            if (lastPath !== 'role') {
                this._activeUserRoleName = lastPath;
            }
        });

        this.userSvc.onUserUpdated.pipe(
            takeUntil(this._allUnsubscribe)
        ).subscribe((res: { updatedUserList: UserInfo[], remainingUserList: UserInfo[] }) => {
            this.updateUserList(res.remainingUserList);
        });

        this._loading = true;

        this.userSvc.getUserRoleList().pipe(
            concatMap((res: { userRoleList: { name: string, desc: string }[], isFault: boolean, errorMessage?: string }) => {
                if (!res.isFault) {
                    this._userRoleList = res.userRoleList.map(ur => ({ name: ur.name, desc: ur.desc, userList: []}));
                    return this.userSvc.getUserList().pipe(
                        map((res: { userList: UserInfo[], isFault: boolean, errorMessage?: string }) => {
                            if (!res.isFault) {
                                this.updateUserList(res.userList);
                            }

                            return true;
                        })
                    )
                }

                return of(true);
            }),
            concatMap(() => this.userSvc.getEnterpriseLicenseUsage()),
            map((res: { license?: { [roleName: string]: { limit: number, usage: number } }, isFault: boolean, errorMessage?: string }) => {
                if (!res.isFault) {
                    this._userRoleUsageList = Object.keys(res.license).map(roleName => Object.assign({ roleName: roleName }, res.license[roleName]));
                }
            })
        ).subscribe(() => {
            this._loading = false;
        });
    }

    private updateUserList(userList: UserInfo[]): void {
        
        const roleMap = userList.reduce((prev, curr) => {
            prev[curr.userRole] = prev[curr.userRole] || [];
            prev[curr.userRole].push(curr);

            return prev;
        }, {});

        this._userRoleList.forEach(ur => ur.userList = roleMap[ur.name] ?? []);
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        this._userRoleList = [];
    }

    viewUserList(role: { id: string, name: string, desc: string, userList?: UserInfo[] }): void {
        this._activeUserRoleName = role.name;
    }
}